import React, { useMemo } from 'react';
import { Form } from '../../../../../../v2BaseComponents/Form';
import { useIsDisabledByParent } from '../../../../../../v2BaseComponents/IsDisabledProvider';
import SideDrawerFormContainer, {
    ConfirmDirtyStateFormCloseContainer,
    SideDrawerDirtyFormExitValidationTracker,
} from '../../../../../../v2BaseComponents/Form/SideDrawerFormContainer';
import useOrganizationClientInvitations from '../../../../../../../hooks/api/useOrganizationClientInvitations';
import useOrganizationInvitationDefinitions from '../../../../../../../hooks/api/useOrganizationInvitationDefinitions';
import OrganizationClientInvitationFields, {
    getDefaultFormValue,
} from '../../../../../../shared/OrganizationClientInvitations/OrganizationClientInvitationFields';
import { AnimateLoadedContent } from '../../../../../../v2BaseComponents/Loader';
import MaybePublicOrgMissingInfoAlert from '../../../../../../shared/MaybePublicOrgMissingInfoAlert';
import { getIntl } from '../../../../../../../utils/intl';

const intl = getIntl('profiles.addClientInvitationSideDrawerContent', {
    send: 'Send',
});

export type AddClientInvitationSideDrawerContentOwnProps = {
    onClose: (force: boolean) => void;
};

export type AddClientInvitationSideDrawerContentProps = AddClientInvitationSideDrawerContentOwnProps;

const AddClientInvitationSideDrawerContent: React.FC<AddClientInvitationSideDrawerContentProps> = ({ onClose }) => {
    const { createClientInvitation, isCreatingClientInvitation } = useOrganizationClientInvitations({
        onCreateInvitation: () => onClose(true),
    });
    const { invitationDefinitions, isLoadingInvitationDefinitions } = useOrganizationInvitationDefinitions({});
    const isDisabled = useIsDisabledByParent(isCreatingClientInvitation);
    const defaultFormValue = useMemo(() => getDefaultFormValue(invitationDefinitions), [invitationDefinitions]);

    return (
        <AnimateLoadedContent isLoading={isCreatingClientInvitation || isLoadingInvitationDefinitions}>
            {() => (
                <ConfirmDirtyStateFormCloseContainer>
                    <Form
                        fillContainer
                        initialValues={defaultFormValue}
                        onSubmit={(values) =>
                            createClientInvitation({
                                ...values,
                                expirationDateTime: values.expirationDateTime
                                    ? new Date(values.expirationDateTime).toISOString()
                                    : undefined,
                            })
                        }
                        isDisabled={isDisabled}
                        forceStackFields
                    >
                        {({ isSubmitting, handleSubmit, values }) => {
                            return (
                                <SideDrawerFormContainer
                                    isDisabled={isDisabled || isSubmitting}
                                    onSubmit={handleSubmit}
                                    onCancel={() => onClose(true)}
                                    saveText={intl('send')}
                                    saveIcon="Send"
                                >
                                    <SideDrawerDirtyFormExitValidationTracker />
                                    <MaybePublicOrgMissingInfoAlert />
                                    <OrganizationClientInvitationFields
                                        invitationDefinitions={invitationDefinitions}
                                        value={values}
                                    />
                                </SideDrawerFormContainer>
                            );
                        }}
                    </Form>
                </ConfirmDirtyStateFormCloseContainer>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(AddClientInvitationSideDrawerContent);
